import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import {
  dashboardSummary,
  incomeExpenseChart,
  expenseCategoryChart,
  profitLossChart,
  creditStatusChart,
  upcomingCollections,
  upcomingPayments,
} from '@/mocks/tr/dashboard';
import { mockCustomers } from '@/mocks/tr/customers';

function SummaryCard({
  title,
  value,
  icon,
  trend,
  color,
}: {
  title: string;
  value: string;
  icon: string;
  trend?: string;
  color: 'gold' | 'green' | 'red' | 'blue' | 'purple' | 'rose' | 'sage' | 'charcoal';
}) {
  const colorMap = {
    gold: 'bg-gold-50 text-gold-600 border-gold-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    rose: 'bg-rose-50 text-rose-600 border-rose-200',
    sage: 'bg-sage-50 text-sage-600 border-sage-200',
    charcoal: 'bg-gray-50 text-gray-600 border-gray-200',
  };

  const iconBgMap = {
    gold: 'bg-gold-100 text-gold-500',
    green: 'bg-green-100 text-green-500',
    red: 'bg-red-100 text-red-500',
    blue: 'bg-blue-100 text-blue-500',
    purple: 'bg-purple-100 text-purple-500',
    rose: 'bg-rose-100 text-rose-500',
    sage: 'bg-sage-100 text-sage-500',
    charcoal: 'bg-gray-100 text-gray-500',
  };

  return (
    <div className={`bg-white rounded-xl border p-5 transition-all hover:shadow-md ${colorMap[color]}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-boutique-warm uppercase tracking-wider mb-1">{title}</p>
          <p className="text-2xl font-semibold text-boutique-black truncate">{value}</p>
          {trend && <p className="text-xs mt-1 opacity-80">{trend}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ml-3 ${iconBgMap[color]}`}>
          <i className={`${icon} text-lg`} />
        </div>
      </div>
    </div>
  );
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Bekliyor': 'bg-gold-50 text-gold-600 border-gold-200',
    'Kısmi Ödendi': 'bg-blue-50 text-blue-600 border-blue-200',
    'Gecikti': 'bg-red-50 text-red-600 border-red-200',
    'Kapandı': 'bg-green-50 text-green-600 border-green-200',
    'Tamamlandı': 'bg-green-50 text-green-600 border-green-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'Gecikti' ? 'bg-red-500' : status === 'Tamamlandı' || status === 'Kapandı' ? 'bg-green-500' : 'bg-gold-500'}`} />
      {status}
    </span>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState('');

  useEffect(() => {
    const date = new Date();
    setCurrentDate(
      date.toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    );
  }, []);

  const topDebtors = mockCustomers
    .filter((c) => c.remaining_debt > 0)
    .sort((a, b) => b.remaining_debt - a.remaining_debt)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('nav.dashboard')}</h1>
          <p className="text-sm text-boutique-warm mt-1">{currentDate}</p>
        </div>
      </div>

      {/* Summary Cards Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title={t('dashboard.todaySales')}
          value={formatCurrency(dashboardSummary.todaySales)}
          icon="ri-shopping-bag-line"
          color="gold"
        />
        <SummaryCard
          title={t('dashboard.todayCollection')}
          value={formatCurrency(dashboardSummary.todayCollection)}
          icon="ri-money-cny-circle-line"
          color="green"
        />
        <SummaryCard
          title={t('dashboard.todayExpense')}
          value={formatCurrency(dashboardSummary.todayExpense)}
          icon="ri-bill-line"
          color="red"
        />
        <SummaryCard
          title={t('dashboard.cashStatus')}
          value={formatCurrency(dashboardSummary.cashStatus)}
          icon="ri-safe-2-line"
          color="charcoal"
        />
      </div>

      {/* Summary Cards Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title={t('dashboard.totalCredit')}
          value={formatCurrency(dashboardSummary.totalCredit)}
          icon="ri-time-line"
          color="rose"
          trend={`${upcomingCollections.length} yaklaşan tahsilat`}
        />
        <SummaryCard
          title={t('dashboard.totalSupplierDebt')}
          value={formatCurrency(dashboardSummary.totalSupplierDebt)}
          icon="ri-truck-line"
          color="purple"
          trend={`${upcomingPayments.length} yaklaşan ödeme`}
        />
        <SummaryCard
          title={t('dashboard.monthlyIncome')}
          value={formatCurrency(dashboardSummary.monthlyIncome)}
          icon="ri-coins-line"
          color="green"
        />
        <SummaryCard
          title={t('dashboard.monthlyProfit')}
          value={formatCurrency(dashboardSummary.monthlyProfit)}
          icon="ri-bar-chart-line"
          color={dashboardSummary.monthlyProfit >= 0 ? 'green' : 'red'}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income-Expense Chart */}
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <h3 className="font-semibold text-boutique-black mb-4 text-sm">Son 30 Gün Gelir-Gider</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={incomeExpenseChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5E3C5" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#B8C4B8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#B8C4B8" tickFormatter={(v) => `${v / 1000}K`} />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }}
              />
              <Line type="monotone" dataKey="gelir" stroke="#D4A843" strokeWidth={2} dot={{ fill: '#D4A843', r: 3 }} />
              <Line type="monotone" dataKey="gider" stroke="#C9A8A0" strokeWidth={2} dot={{ fill: '#C9A8A0', r: 3 }} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Expense Categories Pie */}
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <h3 className="font-semibold text-boutique-black mb-4 text-sm">Gider Kategorileri</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={expenseCategoryChart}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {expenseCategoryChart.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }}
              />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Debtors */}
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('dashboard.topDebtors')}</h3>
            <button
              onClick={() => navigate('/veresiye')}
              className="text-xs text-gold-600 font-medium hover:text-gold-500 transition-colors"
            >
              Tümünü Gör →
            </button>
          </div>
          <div className="space-y-3">
            {topDebtors.map((customer) => (
              <div key={customer.id} className="flex items-center justify-between p-3 rounded-lg bg-cream-50 hover:bg-cream-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center text-gold-600 font-semibold text-xs">
                    {customer.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-boutique-black">{customer.name}</p>
                    <p className="text-xs text-boutique-warm">{customer.phone}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-red-600">{formatCurrency(customer.remaining_debt)}</p>
                  <StatusBadge status={customer.status} />
                </div>
              </div>
            ))}
            {topDebtors.length === 0 && (
              <p className="text-sm text-boutique-warm text-center py-4">{t('common.noData')}</p>
            )}
          </div>
        </div>

        {/* Upcoming Payments */}
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-boutique-black text-sm">{t('dashboard.upcomingSupplier')}</h3>
            <button
              onClick={() => navigate('/tedarikciler')}
              className="text-xs text-gold-600 font-medium hover:text-gold-500 transition-colors"
            >
              Tümünü Gör →
            </button>
          </div>
          <div className="space-y-3">
            {upcomingPayments.map((payment, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-cream-50 hover:bg-cream-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-boutique-blush flex items-center justify-center text-boutique-warm font-semibold text-xs">
                    <i className="ri-truck-line text-sm" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-boutique-black">{payment.supplier}</p>
                    <p className="text-xs text-boutique-warm">{payment.dueDate}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-boutique-black">{formatCurrency(payment.amount)}</p>
                  <span className={`text-xs font-medium ${payment.daysLeft === 0 ? 'text-red-500' : 'text-gold-600'}`}>
                    {payment.daysLeft === 0 ? 'Bugün' : `${payment.daysLeft} gün kaldı`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit-Loss Bar */}
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <h3 className="font-semibold text-boutique-black mb-4 text-sm">Aylık Kâr-Zarar</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={profitLossChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F5E3C5" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#B8C4B8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#B8C4B8" tickFormatter={(v) => `${v / 1000}K`} />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }}
              />
              <Bar dataKey="kazanc" fill="#D4A843" radius={[4, 4, 0, 0]} />
              <Bar dataKey="zarar" fill="#C9A8A0" radius={[4, 4, 0, 0]} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Credit Status */}
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <h3 className="font-semibold text-boutique-black mb-4 text-sm">Veresiye Alacak Durumu</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={creditStatusChart}
                cx="50%"
                cy="50%"
                outerRadius={90}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                labelStyle={{ fontSize: '11px' }}
              >
                {creditStatusChart.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #F5E3C5', fontSize: '12px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}